import { useState, useEffect } from 'react'
import AnimatedNumber from './AnimatedNumber'
import GlowingTrackBar from './GlowingTrackBar'
import HealthOrb from './HealthOrb'
import { safeArray } from '../utils/safe'

// Project definitions with agent assignments
const PROJECTS = [
  {
    id: 'labor',
    name: 'Labor Portal',
    nameAr: 'بوابة العمالة',
    icon: '👷',
    color: '#FF6B35',
    light: '#FF9E6D',
    agents: [
      { name: 'Lead Dev Agent', status: 'active', tasks: 8, lastActive: '2 mins ago' },
      { name: 'QA Agent', status: 'active', tasks: 3, lastActive: '5 mins ago' },
      { name: 'Security Agent', status: 'idle', tasks: 1, lastActive: '1 hour ago' },
    ],
    progress: 85,
    tokens: { daily: 12500, monthly: 375000 },
    metrics: [
      { key: 'backendEndpoints', label: 'API Endpoints', value: 60, icon: '🔌' },
      { key: 'databaseTables', label: 'Database Tables', value: 28, icon: '🗄️' },
      { key: 'flutterScreens', label: 'Flutter Screens', value: 18, icon: '📱' },
      { key: 'testCoverage', label: 'Test Coverage', value: 78, icon: '🧪', format: 'percent' },
    ],
    status: 'live',
    nextMilestone: 'Web Admin Dashboard - Apr 17',
  },
  {
    id: 'rafiq',
    name: 'Rafiq',
    nameAr: 'رفيق',
    icon: '🚗',
    color: '#00BFA5',
    light: '#00E5CC',
    agents: [
      { name: 'Lead Dev Agent', status: 'active', tasks: 5, lastActive: '1 min ago' },
      { name: 'QA Agent', status: 'active', tasks: 2, lastActive: '3 mins ago' },
    ],
    progress: 72,
    tokens: { daily: 9800, monthly: 294000 },
    metrics: [
      { key: 'activeMissions', label: 'Active Missions', value: 0, icon: '🟢' },
      { key: 'totalMissions', label: 'Total Missions', value: 0, icon: '📋' },
      { key: 'driverCount', label: 'Drivers', value: 0, icon: '👤' },
      { key: 'employerCount', label: 'Employers', value: 0, icon: '🏢' },
    ],
    status: 'beta',
    nextMilestone: 'Google Maps Integration - Awaiting API Keys',
  },
  {
    id: 'muwafaq',
    name: 'Muwafaq',
    nameAr: 'موافق',
    icon: '📋',
    color: '#4CAF50',
    light: '#81C784',
    agents: [
      { name: 'Lead Dev Agent', status: 'active', tasks: 4, lastActive: '30 secs ago' },
      { name: 'QA Agent', status: 'active', tasks: 3, lastActive: '2 mins ago' },
      { name: 'Security Agent', status: 'active', tasks: 2, lastActive: '5 mins ago' },
    ],
    progress: 100,
    tokens: { daily: 7500, monthly: 225000 },
    metrics: [
      { key: 'complianceScore', label: 'Compliance %', value: 0, icon: '📊', format: 'percent' },
      { key: 'totalControls', label: 'Total Controls', value: 181, icon: '🛡️' },
      { key: 'verifiedControls', label: 'Verified', value: 0, icon: '✅' },
      { key: 'criticalFindings', label: 'Critical', value: 0, icon: '🔴' },
    ],
    status: 'production',
    nextMilestone: 'Auto Evidence Collection - In Progress',
  },
  {
    id: 'sahha',
    name: 'Sahha',
    nameAr: 'صحة',
    icon: '🏥',
    color: '#448AFF',
    light: '#82B1FF',
    agents: [
      { name: 'Lead Dev Agent', status: 'active', tasks: 3, lastActive: '45 secs ago' },
      { name: 'QA Agent', status: 'idle', tasks: 1, lastActive: '30 mins ago' },
    ],
    progress: 80,
    tokens: { daily: 6500, monthly: 195000 },
    metrics: [
      { key: 'totalClaims', label: 'Total Claims', value: 0, icon: '📋' },
      { key: 'approvedClaims', label: 'Approved', value: 0, icon: '✅' },
      { key: 'rejectedClaims', label: 'Rejected', value: 0, icon: '❌' },
      { key: 'approvalRate', label: 'Approval Rate', value: 0, icon: '📈', format: 'percent' },
    ],
    status: 'beta',
    nextMilestone: 'Clinic Reports - Apr 18',
  },
  {
    id: 'dashboard',
    name: 'Dashboard',
    nameAr: 'لوحة التحكم',
    icon: '📊',
    color: '#8B5CF6',
    light: '#C084FC',
    agents: [
      { name: 'CTO Agent', status: 'active', tasks: 4, lastActive: 'Just now' },
      { name: 'Lead Dev Agent', status: 'idle', tasks: 0, lastActive: '2 hours ago' },
    ],
    progress: 100,
    tokens: { daily: 3200, monthly: 96000 },
    metrics: [
      { key: 'components', label: 'Components', value: 18, icon: '🧩' },
      { key: 'apiEndpoints', label: 'API Endpoints', value: 8, icon: '🔌' },
      { key: 'realTimeUpdates', label: 'Real-time Updates', value: 30, icon: '⚡', format: 'seconds' },
      { key: 'userSessions', label: 'User Sessions', value: 1, icon: '👤' },
    ],
    status: 'production',
    nextMilestone: 'Multi-tenant Support - Planned',
  },
]

// Mock API for agent activity
const fetchAgentActivity = async () => {
  // Simulate API call
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({
        timestamp: new Date().toISOString(),
        totalAgents: 7,
        activeAgents: 5,
        dailyTokens: 39500,
        monthlyTokens: 1185000,
        recentActivities: [
          { agent: 'Lead Dev Agent', action: 'Committed Labor Portal Flutter screens', project: 'labor', time: '2 mins ago' },
          { agent: 'QA Agent', action: 'Completed Rafiq test suite', project: 'rafiq', time: '5 mins ago' },
          { agent: 'CTO Agent', action: 'Enhanced dashboard analytics', project: 'dashboard', time: '10 mins ago' },
          { agent: 'Security Agent', action: 'Reviewed Muwafaq controls', project: 'muwafaq', time: '15 mins ago' },
        ]
      })
    }, 500)
  })
}

export default function RealTimeProjectStatus() {
  const [agentData, setAgentData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState(null)

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchAgentActivity()
        setAgentData(data)
        setLastUpdate(new Date())
      } catch (error) {
        console.error('Failed to load agent data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
    const interval = setInterval(loadData, 30000) // Update every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const formatToken = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
    return value.toString()
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'production': return '#22C55E'
      case 'live': return '#22C55E'
      case 'beta': return '#F59E0B'
      case 'development': return '#3B82F6'
      default: return '#64748B'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'production': return 'Production'
      case 'live': return 'Live'
      case 'beta': return 'Beta'
      case 'development': return 'Development'
      default: return status
    }
  }

  return (
    <div className="space-y-6">
      {/* Header with agent summary */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">📊 Real-time Project Status</h2>
          <p className="text-sm text-[#94A3B8]">Live monitoring of all 5 projects with agent activity</p>
        </div>
        <div className="flex items-center gap-4">
          {agentData && (
            <div className="text-right">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-medium text-white">{agentData.activeAgents}/{agentData.totalAgents} agents active</span>
              </div>
              <p className="text-xs text-[#94A3B8]">
                Tokens today: <span className="font-bold text-amber-400">{formatToken(agentData.dailyTokens)}</span>
              </p>
            </div>
          )}
          {lastUpdate && (
            <div className="text-xs text-[#64748B]">
              Updated: {lastUpdate.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {PROJECTS.map((project) => (
          <div
            key={project.id}
            className="relative overflow-hidden rounded-[14px] bg-surface border border-white/[0.06] p-5 hover:border-white/[0.10] transition-all duration-150"
          >
            {/* Project header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{project.icon}</span>
                <div>
                  <h3 className="text-base font-bold text-white">{project.nameAr}</h3>
                  <p className="text-xs text-[#94A3B8]">{project.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="px-2 py-1 rounded-full text-[10px] font-medium"
                  style={{
                    backgroundColor: `${getStatusColor(project.status)}20`,
                    color: getStatusColor(project.status),
                    border: `1px solid ${getStatusColor(project.status)}40`
                  }}
                >
                  {getStatusLabel(project.status)}
                </span>
                <HealthOrb status="healthy" size="sm" />
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[#94A3B8]">Overall Progress</span>
                <span className="text-sm font-bold" style={{ color: project.color }}>{project.progress}%</span>
              </div>
              <GlowingTrackBar
                progress={project.progress}
                color={project.color}
                lightColor={project.light}
                height="h-2.5"
              />
            </div>

            {/* Metrics grid */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {project.metrics.map((metric) => (
                <div key={metric.key} className="rounded-[10px] bg-base border border-white/[0.04] p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-xs">{metric.icon}</span>
                    <span className="text-[10px] font-medium text-[#64748B]">{metric.label}</span>
                  </div>
                  <p className="text-lg font-extrabold tabular-nums" style={{ color: project.color }}>
                    {metric.format === 'percent' ? `${metric.value}%` : 
                     metric.format === 'seconds' ? `${metric.value}s` :
                     typeof metric.value === 'number' ? <AnimatedNumber value={metric.value} duration={500} /> : metric.value}
                  </p>
                </div>
              ))}
            </div>

            {/* Agent activity */}
            <div className="border-t border-white/[0.06] pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#94A3B8]">Agent Activity</span>
                <span className="text-[10px] text-[#64748B]">{project.agents.filter(a => a.status === 'active').length} active</span>
              </div>
              <div className="space-y-2">
                {project.agents.map((agent, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${agent.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-[#64748B]'}`} />
                      <span className="text-xs text-white truncate max-w-[100px]">{agent.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#64748B]">{agent.tasks} tasks</span>
                      <span className="text-[10px] text-[#64748B]">{agent.lastActive}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Token usage */}
            <div className="mt-3 pt-3 border-t border-white/[0.06]">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[#64748B]">Token Usage</span>
                <div className="text-right">
                  <p className="text-[10px] font-medium text-white">
                    <span className="text-amber-400">{formatToken(project.tokens.daily)}</span>/day
                  </p>
                  <p className="text-[9px] text-[#64748B]">{formatToken(project.tokens.monthly)}/month</p>
                </div>
              </div>
            </div>

            {/* Next milestone */}
            <div className="mt-3 pt-3 border-t border-white/[0.06]">
              <p className="text-[10px] text-[#64748B]">Next Milestone</p>
              <p className="text-xs text-white truncate">{project.nextMilestone}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Agent Activity Feed */}
      {agentData && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">🤖 Recent Agent Activities</h3>
            <div className="flex items-center gap-2">
              <div className="px-3 py-1 rounded-full bg-base border border-white/[0.06]">
                <span className="text-xs text-white">Total Tokens: <span className="font-bold text-amber-400">{formatToken(agentData.monthlyTokens)}</span>/month</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {safeArray(agentData.recentActivities).map((activity, idx) => (
              <div key={idx} className="rounded-[12px] bg-base border border-white/[0.06] p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/[0.04] flex items-center justify-center">
                    <span className="text-sm">🤖</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{activity.agent}</p>
                    <p className="text-xs text-[#94A3B8] mt-1">{activity.action}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] text-[#94A3B8]">
                        {activity.project}
                      </span>
                      <span className="text-[10px] text-[#64748B]">{activity.time}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
